import type { ChangeEvent } from 'react';

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  IconButton,
  InputDescription,
  Modal,
  PrimaryButton,
  SecondaryButton,
  Spinner,
  TextInput,
} from '@movingimage-evp/mi-ui-component-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './video-url-modal.module.css';

type VideoUrlModalProps = {
  isOpen: boolean;
  loading?: boolean;
  initialVideoVodManifestUrl?: string | null;
  onSaveVideoVodManifestUrl: (videoVodManifestUrl: string) => void;
  onSelectVideoFromVmpro: () => void;
  onClose: () => void;
};

export function VideoUrlModal({
  isOpen,
  loading = false,
  initialVideoVodManifestUrl,
  onSaveVideoVodManifestUrl,
  onSelectVideoFromVmpro,
  onClose,
}: VideoUrlModalProps) {
  const { t } = useTranslation();
  const [videoVodManifestUrlViewOpen, setVideoVodManifestUrlViewOpen] = useState(Boolean(initialVideoVodManifestUrl));
  const [videoVodManifestUrl, setVideoVodManifestUrl] = useState(initialVideoVodManifestUrl || '');
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setVideoVodManifestUrl((isOpen && initialVideoVodManifestUrl) || '');
    setVideoVodManifestUrlViewOpen(isOpen && Boolean(initialVideoVodManifestUrl));
  }, [isOpen, initialVideoVodManifestUrl]);

  const isValidUrl = (string: string) => {
    try {
      new URL(string);
      return true;
    } catch {
      return false;
    }
  };

  const onChangeVideoVodManifestUrl = (event: ChangeEvent<HTMLInputElement>) => {
    const url = event.target.value;

    setVideoVodManifestUrl(url);
    setError(
      !isValidUrl(url) && url !== '' ? t('manager.webcastSetup.media.videoUrlModal.invalidUrlError') : undefined
    );
  };

  return (
    <Modal
      data-testid="video-source-selection-modal"
      isOpen={isOpen}
      onClose={onClose}
      className={styles.sourceSelectionModal}
      title={
        <div className={styles.title}>
          {videoVodManifestUrlViewOpen && (
            <IconButton
              naked
              rounded
              className={styles.backButton}
              aria-label={t('manager.webcastSetup.media.videoUrlModal.back')}
              onClick={() => setVideoVodManifestUrlViewOpen(false)}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}

          {t('manager.webcastSetup.media.videoUrlModal.title')}
        </div>
      }
      footer={
        videoVodManifestUrlViewOpen && (
          <div className={styles.footer}>
            <SecondaryButton data-testid="cancel-set-custom-hls-url-button" type="button" onClick={onClose}>
              {t('common.cancel')}
            </SecondaryButton>

            <PrimaryButton
              data-testid="save-custom-hls-url-button"
              disabled={!videoVodManifestUrl || !!error}
              onClick={() => onSaveVideoVodManifestUrl(videoVodManifestUrl)}
            >
              {loading ? <Spinner /> : t('common.save')}
            </PrimaryButton>
          </div>
        )
      }
    >
      {videoVodManifestUrlViewOpen && (
        <InputDescription
          inputId="custom-hls-url-input"
          label={t('manager.webcastSetup.media.videoUrlModal.videoVodManifestUrlLabel')}
          errorMessage={error}
        >
          <TextInput
            data-testid="custom-hls-url-input"
            autoFocus
            value={videoVodManifestUrl}
            onChange={onChangeVideoVodManifestUrl}
            placeholder={t('manager.webcastSetup.media.videoUrlModal.videoVodManifestUrlPlaceholder')}
          />
        </InputDescription>
      )}

      {!videoVodManifestUrlViewOpen && (
        <div className={styles.sourceSelectionWrapper}>
          <SecondaryButton
            data-testid="add-video-with-custom-hls-url-button"
            className={styles.selectionButton}
            onClick={() => setVideoVodManifestUrlViewOpen(true)}
          >
            {t('manager.webcastSetup.media.videoUrlModal.addVideoWithVideoVodManifestUrl')}
            <ChevronRightIcon />
          </SecondaryButton>

          <SecondaryButton
            data-testid="select-video-from-vmpro-button"
            className={styles.selectionButton}
            onClick={onSelectVideoFromVmpro}
          >
            {t('manager.webcastSetup.media.videoUrlModal.selectVideoFromVmpro')}
            <ChevronRightIcon />
          </SecondaryButton>
        </div>
      )}
    </Modal>
  );
}
